import { ReportData } from '../types/report.types';
import { isFindingClosed } from './findingsUtils';

export interface AttestationData {
  enabled: boolean;
  date: string;
  reference: string;
  client_name: string;
  assessment_type: string;
  approach: string;
  scope: string;
  testing_window: string;
  report_date: string;
  total_findings: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  remediation_verified: boolean;
  retest_date: string;
  remediation_text?: string;
}

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

/**
 * Format a date value as "DD Mon YYYY" (e.g. "22 Sep 2026")
 */
export const formatAttestationDate = (dateVal: any): string => {
  if (!dateVal) return '';
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return String(dateVal);
  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTHS_SHORT[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};

/**
 * Format start and end date as "DD Mon – DD Mon YYYY" (e.g. "22 Sep – 03 Oct 2026")
 */
export const formatTestingWindow = (start: any, end: any, fallbackDate?: any): string => {
  if (!start && !end) {
    if (fallbackDate) {
      return formatAttestationDate(fallbackDate);
    }
    return '22 Sep – 03 Oct 2026';
  }

  if (start && !end) return formatAttestationDate(start);
  if (!start && end) return formatAttestationDate(end);

  const s = new Date(start);
  const e = new Date(end);

  if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
    const sDay = String(s.getDate()).padStart(2, '0');
    const eDay = String(e.getDate()).padStart(2, '0');
    const sMonth = MONTHS_SHORT[s.getMonth()];
    const eMonth = MONTHS_SHORT[e.getMonth()];
    const sYear = s.getFullYear();
    const eYear = e.getFullYear();

    if (sYear === eYear && sMonth === eMonth) {
      return `${sDay} – ${eDay} ${sMonth} ${sYear}`;
    }
    if (sYear === eYear) {
      return `${sDay} ${sMonth} – ${eDay} ${eMonth} ${sYear}`;
    }
    return `${sDay} ${sMonth} ${sYear} – ${eDay} ${eMonth} ${eYear}`;
  }

  return `${start} – ${end}`;
};

/**
 * Standardize assessment type string
 */
export const normalizeAssessmentType = (type?: string): string => {
  if (!type || !type.trim()) {
    return 'Web Application Penetration Test';
  }
  const clean = type.trim();
  const lower = clean.toLowerCase();
  if (
    lower.includes('penetration test') ||
    lower.includes('security assessment') ||
    lower.includes('review') ||
    lower.includes('pentest')
  ) {
    return clean;
  }
  return `${clean} Penetration Test`;
};

/**
 * Extract clean in-scope assets summary
 */
export const extractScopeSummary = (reportData: ReportData): string => {
  const targets = reportData.target_details || [];
  if (targets.length > 0) {
    const targetValues = targets
      .map(t => (t.target_value || t.url || t.target_name || '').trim())
      .filter(Boolean);

    if (targetValues.length > 0) {
      if (targetValues.length <= 3) {
        return targetValues.join(', ');
      }
      return `${targetValues.slice(0, 3).join(', ')} (+${targetValues.length - 3} more)`;
    }
  }

  if (reportData.scope && typeof reportData.scope === 'string') {
    // Strip HTML tags if any
    const text = reportData.scope
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (text) {
      return text.length > 120 ? `${text.substring(0, 117)}...` : text;
    }
  }

  if (reportData.program_name) {
    return reportData.program_name;
  }

  return 'In-scope application and infrastructure targets';
};

/**
 * Extracts and calculates all dynamic fields for the Letter of Attestation.
 * Handles saved overrides from finding_config.attestation_letter / certificate.
 */
export const getAttestationData = (reportData: ReportData): AttestationData => {
  const cfg: any =
    (reportData?.finding_config as any)?.attestation_letter ||
    (reportData?.finding_config as any)?.certificate ||
    {};

  // 0. Master Toggle - enabled (default: false - requires QA/Admin explicit toggle)
  const enabled = cfg.enabled === true || cfg.is_enabled === true;

  // 1. Date
  const defaultDate = formatAttestationDate(
    reportData.approved_by_date ||
    reportData.current_date ||
    reportData.date_of_request ||
    new Date()
  );
  const date = cfg.date || defaultDate;

  // 2. Reference
  const defaultReference =
    reportData.document_number ||
    (reportData.version_number ? `CTB-${reportData.version_number}` : 'CTB-TECH-CYBER-001-V.01');
  const reference = cfg.reference || defaultReference;

  // 3. Client Name
  const client_name =
    cfg.client_name ||
    reportData.company_name ||
    'Client Organisation';

  // 4. Assessment Type
  const rawType =
    cfg.assessment_type ||
    reportData.testing_type ||
    reportData.program_details?.[0]?.testing_type;
  const assessment_type = normalizeAssessmentType(rawType);

  // 5. Approach
  const approach = cfg.approach || 'Graybox';

  // 6. Scope
  const scope = cfg.scope || extractScopeSummary(reportData);

  // 7. Testing Window
  const testing_window =
    cfg.testing_window ||
    formatTestingWindow(reportData.start_date, reportData.end_date, reportData.current_date);

  // 8. Confidential Report Delivery Date
  const report_date =
    cfg.report_date ||
    formatAttestationDate(
      reportData.approved_by_date ||
      reportData.current_date ||
      reportData.date_of_request ||
      reportData.start_date ||
      new Date()
    );

  // 9. Finding Counts
  const findings = reportData.detailed_findings || [];
  const countBySeverity = (sev: string): number => {
    return findings.filter(
      f => (f.severity_category || f.severity || '').toLowerCase() === sev.toLowerCase()
    ).length;
  };

  const critical_count =
    typeof cfg.critical_count === 'number'
      ? cfg.critical_count
      : (reportData.severity_counts?.Critical ??
         reportData.open_close_counts_by_severity?.Critical?.Total ??
         countBySeverity('critical'));

  const high_count =
    typeof cfg.high_count === 'number'
      ? cfg.high_count
      : (reportData.severity_counts?.High ??
         reportData.open_close_counts_by_severity?.High?.Total ??
         countBySeverity('high'));

  const medium_count =
    typeof cfg.medium_count === 'number'
      ? cfg.medium_count
      : (reportData.severity_counts?.Medium ??
         reportData.open_close_counts_by_severity?.Medium?.Total ??
         countBySeverity('medium'));

  const low_count =
    typeof cfg.low_count === 'number'
      ? cfg.low_count
      : (reportData.severity_counts?.Low ??
         reportData.open_close_counts_by_severity?.Low?.Total ??
         countBySeverity('low'));

  const total_findings =
    typeof cfg.total_findings === 'number'
      ? cfg.total_findings
      : (reportData.total_findings ??
         (critical_count + high_count + medium_count + low_count));

  // 10. Remediation Verified block condition
  let autoRemediationVerified = false;
  if (typeof reportData.total_closed === 'number' && reportData.total_closed > 0) {
    autoRemediationVerified = true;
  } else if (findings.length > 0 && findings.some(f => isFindingClosed(reportData, f))) {
    autoRemediationVerified = true;
  }

  const remediation_verified =
    typeof cfg.remediation_verified === 'boolean'
      ? cfg.remediation_verified
      : autoRemediationVerified;

  const defaultRetestDate = formatAttestationDate(
    reportData.revision_date ||
    (reportData as any).updated_at ||
    reportData.current_date ||
    new Date()
  );
  const retest_date = cfg.retest_date || defaultRetestDate;

  const defaultRemediationText = `A verification retest was performed on ${retest_date}. All findings rated Critical and High have been remediated and confirmed closed.`;
  const remediation_text = cfg.remediation_text || defaultRemediationText;

  return {
    enabled,
    date,
    reference,
    client_name,
    assessment_type,
    approach,
    scope,
    testing_window,
    report_date,
    total_findings,
    critical_count,
    high_count,
    medium_count,
    low_count,
    remediation_verified,
    retest_date,
    remediation_text
  };
};

/**
 * Checks whether the Letter of Attestation has been enabled by QA/Admin.
 * Defaults to false (off).
 */
export const isAttestationEnabled = (reportData: any): boolean => {
  if (!reportData) return false;
  let parsedConfig: any = {};
  if (reportData.finding_config) {
    if (typeof reportData.finding_config === 'string') {
      try {
        parsedConfig = JSON.parse(reportData.finding_config);
      } catch (_) {}
    } else {
      parsedConfig = reportData.finding_config;
    }
  }
  const loa = parsedConfig?.attestation_letter || parsedConfig?.certificate;
  if (!loa) return false;
  return loa.enabled === true || loa.is_enabled === true;
};
